import { useEffect, useRef, useState } from 'react';
import './NewsPreview.css';

export interface PreviewArticle {
  title: string;
  date: string;
  content: string;
  image: string;
  images: string[];
}

interface Props {
  clubId: string;
  article: PreviewArticle;
}

/** The widths the site is drawn at. Desktop is scaled down to fit the box. */
const WIDTHS = { desktop: 1200, mobile: 390 } as const;

/**
 * The article as the club's own site will show it, beside the editor. The
 * frame is the real website (/preview/news on the club's address, see the
 * website's pages/NewsPreview.tsx), so the theme, colours and fonts are the
 * club's own. The unsaved draft is posted into it as the club types; nothing
 * is saved until Save Draft or Publish.
 */
function NewsPreview({ clubId, article }: Props) {
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<keyof typeof WIDTHS>('desktop');
  const [size, setSize] = useState({ width: 0, height: 0 });
  // The page says when it is listening, and from which address: a club on its
  // own domain answers from there. Counted, so a reload is sent the draft again.
  const [ready, setReady] = useState<{ origin: string; count: number } | null>(null);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow || e.data?.type !== 'gfc-news-preview-ready') return;
      setReady(r => ({ origin: e.origin, count: (r?.count || 0) + 1 }));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!ready) return;
    const t = window.setTimeout(() => {
      frame.current?.contentWindow?.postMessage({ type: 'gfc-news-preview', article }, ready.origin);
    }, 150);
    return () => window.clearTimeout(t);
  }, [ready, article]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const width = WIDTHS[mode];
  const scale = size.width ? Math.min(1, size.width / width) : 1;

  return (
    <div className="news-preview">
      <div className="news-preview-head">
        <span className="news-preview-label">Preview</span>
        <div className="news-preview-modes" role="group" aria-label="Preview size">
          {(Object.keys(WIDTHS) as Array<keyof typeof WIDTHS>).map(m => (
            <button
              key={m}
              type="button"
              className={`news-preview-mode${mode === m ? ' is-on' : ''}`}
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
            >
              {m === 'desktop' ? 'Desktop' : 'Mobile'}
            </button>
          ))}
        </div>
      </div>
      <div className="news-preview-box" ref={box}>
        <iframe
          ref={frame}
          title="How the article will look on your website"
          src={`https://${clubId}.grassrootsfc.co.uk/preview/news`}
          style={{
            width,
            height: size.height ? size.height / scale : '100%',
            left: Math.max(0, (size.width - width * scale) / 2),
            transform: `scale(${scale})`,
          }}
        />
      </div>
      <p className="news-preview-note">
        How it will look on your website. Nothing changes there until you press Save Draft or Publish.
      </p>
    </div>
  );
}

export default NewsPreview;
